/* eslint-disable @typescript-eslint/consistent-type-assertions */

import { setup } from 'xstate5';

import { ScanToPayActions } from './scan-to-pay.machine.actions';
import { ScanToPayActors } from './scan-to-pay.machine.actors';
import { initialContext, type ScanToPayContext } from './scan-to-pay.machine.context';
import { ScanToPayDelays } from './scan-to-pay.machine.delays';
import { type ScanToPayEvents } from './scan-to-pay.machine.events';
import { ScanToPayGuards } from './scan-to-pay.machine.guards';

// ─── Machine ─────────────────────────────────────────────────────────────────

export const scanToPayMachine = setup({
  types: {
    context: {} as ScanToPayContext,
    events: {} as ScanToPayEvents,
  },
  actions: ScanToPayActions,
  actors: ScanToPayActors,
  guards: ScanToPayGuards,
  delays: ScanToPayDelays,
}).createMachine({
  id: 'scan-to-pay-state-machine',
  context: initialContext,
  initial: 'waiting',
  states: {
    waiting: {
      id: 'scan-to-pay-waiting',
      description: 'Waiting for the machine to be initialized.',
    },
    fetching: {
      id: 'scan-to-pay-fetching',
      description: `Fetching the initial data.`,
      invoke: {
        id: 'fetchData',
        src: 'fetchData',
        onError: '#scan-to-pay-failed',
        onDone: {
          actions: ['onFetchDataDone'],
          target: '#scan-to-pay-ready',
        },
      },
    },
    ready: {
      id: 'scan-to-pay-ready',
      description: `Initial data is ready.`,
      type: 'parallel',
      states: {
        standard: {
          initial: 'idle',
          states: {
            idle: {
              id: 'idle',
              description: 'Ready to receive events.',
              on: {
                REQUEST_LOYALTY_INFO: '#loyalty-info',
                REQUEST_FAQ: '#faq',
                SWITCH_USE_CREDIT: {
                  target: ['#idle', '#payment-token-fetching'],
                  actions: ['onSwitchUseCredit'],
                },
              },
            },
            loyaltyInfo: {
              id: 'loyalty-info',
              on: {
                CANCEL_REQUEST: '#idle',
              },
            },
            faq: {
              id: 'faq',
              on: {
                CANCEL_REQUEST: '#idle',
              },
            },
          },
        },
        identifyToken: {
          initial: 'fetching',
          states: {
            fetching: {
              id: 'identify-token-fetching',
              invoke: {
                id: 'fetchIdentifyToken',
                src: 'fetchIdentifyToken',
                onError: '#scan-to-pay-failed',
                onDone: {
                  target: 'polling',
                  actions: ['onFetchIdentifyTokenDone'],
                },
              },
            },
            polling: {
              on: {
                REFETCH_IDENTIFY_TOKEN: 'fetching',
              },
              after: {
                TOKEN_POLLING_DELAY: {
                  target: '#identify-token-fetching',
                },
              },
            },
          },
        },
        paymentToken: {
          initial: 'checking',
          states: {
            checking: {
              always: {
                target: 'fetching',
                guard: 'shouldFetchPaymentToken',
              },
            },
            fetching: {
              id: 'payment-token-fetching',
              invoke: {
                id: 'fetchPaymentToken',
                src: 'fetchPaymentToken',
                input: ({ context }) => context,
                onError: '#scan-to-pay-failed',
                onDone: {
                  target: 'polling',
                  actions: ['onFetchPaymentTokenDone'],
                },
              },
            },
            polling: {
              on: {
                REFETCH_PAYMENT_TOKEN: 'checking',
              },
              after: {
                TOKEN_POLLING_DELAY: {
                  target: '#payment-token-fetching',
                },
              },
            },
          },
        },
        credits: {
          initial: 'polling',
          states: {
            fetching: {
              id: 'credits-fetching',
              invoke: {
                id: 'fetchCredits',
                src: 'fetchCredits',
                onError: 'polling',
                onDone: {
                  target: 'polling',
                  actions: ['onFetchCreditsDone'],
                },
              },
            },
            polling: {
              on: {
                REFETCH_CREDITS: 'fetching',
              },
              after: {
                CREDITS_POLLING_DELAY: 'fetching',
              },
            },
          },
        },
      },
    },
    failed: {
      id: 'scan-to-pay-failed',
      description: 'A failed state that awaits for another start event.',
    },
  },
  on: {
    START: '#scan-to-pay-fetching',
  },
});
